import { Spacing } from '@persona-id/design-tokens';
import { DateTime } from 'luxon';
import styled from 'styled-components';

import { Blueprint, BlueprintBlockType } from 'dashboard/models/blueprint';
import { Inquiry } from 'dashboard/models/inquiry';
import {
  DashboardPageLink,
  GuideStepTipBanner,
  UnorderedList,
} from 'dashboard/views/DashboardSandboxHomeView/constants';
import Button from 'design-system/components/Button';
import Select, { Option } from 'design-system/components/Select';
import Spacer from 'design-system/components/Spacer';
import { Paragraph } from 'design-system/components/Text';
import { DATE_AND_TIME_TZ_FORMAT } from 'lib/constants/luxonFormats';
import { Iso8601DateString } from 'lib/types';

interface ReviewCompletedInquiryStepProps {
  inquiriesForSelectedBlueprint: Inquiry[];
  selectedInquiry: Inquiry;
  selectedBlueprint: Blueprint;
  isLoadingInquiriesForSelectedBlueprint: boolean;
  isStepCompleted: boolean;
  isSavingCompletedStep: boolean;
  handleLoadInquiriesForSelectedBlueprint: (inquiryTemplateIds: string[]) => Promise<void>;
  setSelectedInquiry: (inquiry: Inquiry) => void;
  saveCompletedStep: () => void;
}

const ReviewCompletedInquiryStep = ({
  inquiriesForSelectedBlueprint,
  selectedInquiry,
  selectedBlueprint,
  isLoadingInquiriesForSelectedBlueprint,
  isStepCompleted,
  isSavingCompletedStep,
  handleLoadInquiriesForSelectedBlueprint,
  setSelectedInquiry,
  saveCompletedStep,
}: ReviewCompletedInquiryStepProps) => {
  const handleSelectedInquiryChange = (value: string) => {
    const inquiryObj = inquiriesForSelectedBlueprint.filter((inq) => inq.id === value)[0];
    setSelectedInquiry(inquiryObj);
  };

  return (
    <>
      <Paragraph color='primary'>
        Congrats on running your first Inquiry! Let’s review the results together.
      </Paragraph>
      <Spacer height={2} />
      <Paragraph color='primary'>
        Persona collects everything from the inputted data, the extracted data from captured images,
        metadata collected from the device, and behavioral signals that can be indicative of risk.
      </Paragraph>
      <Spacer height={2} />
      <Paragraph color='primary'>
        Select your completed Inquiry to view the information collected:
      </Paragraph>
      <Spacer height={1} />
      <Select
        aria-label='Select a completed Inquiry'
        disabled={
          isLoadingInquiriesForSelectedBlueprint || inquiriesForSelectedBlueprint.length === 0
        }
        placeholder={
          inquiriesForSelectedBlueprint.length === 0
            ? 'No completed Inquiries for this solution yet!'
            : 'Choose a completed Inquiry...'
        }
        onValueChange={(value) => handleSelectedInquiryChange(value)}
      >
        {inquiriesForSelectedBlueprint.map((inquiry) => (
          <Option key={`option-${inquiry.id}`} value={inquiry.id}>
            Inquiry created on{' '}
            {DateTime.fromISO(inquiry.attributes['created-at'] as Iso8601DateString).toFormat(
              DATE_AND_TIME_TZ_FORMAT,
            )}{' '}
            ({inquiry.id})
          </Option>
        ))}
      </Select>
      <Spacer height={1} />
      <ButtonRow>
        {isSavingCompletedStep ? (
          <Button disabled width='button'>
            Saving...
          </Button>
        ) : (
          <Button
            id='home-sandbox-onboarding-checklist__view-completed-inquiry'
            disabled={!selectedInquiry || isLoadingInquiriesForSelectedBlueprint}
            width='button'
            onClick={() => {
              if (!isStepCompleted) {
                saveCompletedStep();
              }
              window.open(DashboardPageLink.Inquiry.replace('[inquiry-token]', selectedInquiry.id));
            }}
          >
            View the Inquiry
          </Button>
        )}
        <Button
          id='home-sandbox-onboarding-checklist__refresh-inquiry-list'
          disabled={isLoadingInquiriesForSelectedBlueprint}
          variant='basic'
          width='button'
          onClick={() => {
            const inquiryTemplateBlocks = selectedBlueprint.attributes.blocks.filter(
              (block) => block.type === BlueprintBlockType.InquiryTemplate,
            );
            if (inquiryTemplateBlocks.length > 0) {
              handleLoadInquiriesForSelectedBlueprint(
                inquiryTemplateBlocks.map((itmpl) => itmpl.token),
              );
            }
          }}
        >
          Reload Inquiries
        </Button>
      </ButtonRow>
      <Spacer height={3} />
      <GuideStepTipBanner>
        <Paragraph color='primary'>
          Tip: See our Help Center to learn more about the details of a completed Inquiry:
        </Paragraph>
        <UnorderedList>
          <li>
            <a
              href='https://support.withpersona.com/hc/en-us/articles/13314421749139-Inquiry-details-in-the-Dashboard'
              target='_blank'
              rel='noreferrer'
            >
              Inquiry details in the Dashboard
            </a>
          </li>
        </UnorderedList>
      </GuideStepTipBanner>
    </>
  );
};

const ButtonRow = styled('div')`
  display: flex;
  flex-direction: row;
  column-gap: ${Spacing[2]};
`;

export default ReviewCompletedInquiryStep;
